import React, {
  useState,
  useEffect,
  type HTMLAttributes,
  type MouseEvent,
  type ReactElement,
} from "react";
import {
  IconFa,
  Box,
  PopOver,
  Text,
  Heading,
} from "@cruk/cruk-react-components";
import {
  faEnvelope,
  faComment,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import { useSWRConfig } from "swr";
import {
  faFacebookF,
  faFacebookMessenger,
  faWhatsapp,
  faLinkedinIn,
  faXTwitter,
  faInstagram,
  faBluesky,
} from "@fortawesome/free-brands-svg-icons";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { fetcher } from "@fwa/src/services/apiClient";
import { getFwsUrlForPage } from "@fwa/src/utils/pageUtils";
import {
  isMobile,
  isChrome,
  hasShareApi,
  isBrowser,
} from "@fwa/src/utils/browserUtils";
import {
  getShareContentFromEntity,
  getShareURLsFromEntity,
} from "@fwa/src/utils/shareUtils";
import {
  fwsUrlPage,
  fwsUrlPageShareCount,
} from "@fwa/src/services/pageService";

import { useOptimizelyContext } from "@fwa/src/contexts/OptimizelyContext";

import { CopyToClipBoard } from "@fwa/src/components/CopyToClipboard";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/Share/styles.module.css";

import {
  type FeedItemType,
  type PageType,
  type ShareURLsObjectType,
} from "@fwa/src/types";
import {
  faNoteSticky,
  faPaperPlane,
} from "@fortawesome/free-regular-svg-icons";

export const Share = ({
  children,
  isOwner,
  page,
  feedItem,
  popoverPosition = "bottom",
  fixedPosition = false,
  ctaLocation = "",
  className,
  style,
}: {
  page: PageType;
  feedItem: FeedItemType | null;
  isOwner: boolean;
  popoverPosition?: "top" | "left" | "right" | "bottom";
  fixedPosition?: boolean;
  ctaLocation: string;
  children: ReactElement<HTMLAttributes<HTMLElement>>;
  className?: string;
  style?: React.CSSProperties;
}) => {
  const { trackEventOptimizely } = useOptimizelyContext();
  const entityType = feedItem?.entityType || page?.entityType || "";
  const { trackError, trackEventGtm } = useTracking();
  const [nativeShareIsOk, setNativeShareIsOk] = useState(true);
  const [showInstaHint, setShowInstaHint] = useState(false);
  // we want this to be set when we have access to window.navigator.share and browser user agent
  const [showMobile, setShowMobile] = useState(true);
  const [showShareApi, setShowShareApi] = useState(false);
  const [shareObject, setShareObject] = useState<ShareURLsObjectType | null>(
    null,
  );

  const { mutate: globalMutate } = useSWRConfig();

  const bumpPageShareCount = async () => {
    // This only works for FRP at the minute.
    if (page.entityType !== "FundraisingPage") return;
    // if (!feedItem?.page) return;
    const pageId = feedItem?.page.uniqueId || page.uniqueId;
    if (!isOwner) {
      // logged out user bumps generic share count
      const fwsShareCountUrl = fwsUrlPageShareCount(
        feedItem?.page.entityType || page.entityType,
        pageId,
      );
      const fwsShareCount = await fetcher<{ sharesCount: number }>(
        fwsShareCountUrl,
      ).catch((error) => {
        trackError(error as Error, {
          function: "fwsSharesCount",
        });
      });
      if (!fwsShareCount) {
        trackError(new Error("no res from fwsShareCountUrl"), {
          function: "fwsSharesCount",
        });
        return;
      }

      const newPageData = {
        ...(feedItem?.page || page),
        ...fwsShareCount,
      };
      const pageToMutate = feedItem?.page || page;
      const pageKey = getFwsUrlForPage(pageToMutate);
      globalMutate(pageKey, newPageData, false).catch(() => {
        trackError(new Error("Could not update sharesCount locally"), {
          function: "mutatePage in bumpShareCount",
        });
      });
    } else {
      // logged in user bumps owner share count
      const slug = getFwsUrlForPage(feedItem?.page || page);
      const url = fwsUrlPage({ pageId });
      const ownerShareCount =
        (feedItem?.page.ownerShareCount || page.ownerShareCount || 0) + 1;
      const updatedPageData = await fetcher(url, {
        method: "PATCH",
        body: JSON.stringify({ ownerShareCount }),
      })
        .then((pageData) => pageData as PageType)
        .catch((error) => {
          trackError(error as Error, {
            function: "bumpOwnerSharesCount",
          });
        });

      if (!updatedPageData) {
        trackError(
          new Error("no updated page returned from bumpOwnerSharesCount"),
          {
            function: "fwsSharesCount",
          },
        );
        return;
      }
      globalMutate(slug, updatedPageData, false).catch(() => {
        trackError(new Error("Could not update ownerSharesCount locally"), {
          function: "mutatePage in bumpOwnerSharesCount",
        });
      });
    }
  };

  // tracking for cta click event happens before a confirmed share event
  const sendCtaTrackingEvent = () => {
    trackEventGtm({
      event: "share_cta",
      cta: ctaLocation,
      entityType,
      isOwner,
    });
    if (ctaLocation === "feedItem_shareprompt_modal") {
      trackEventOptimizely({
        eventKey: "of_share_new_post",
      });
    }
    trackEventOptimizely({
      eventKey: "of_share",
    });

    if (isOwner) {
      trackEventOptimizely({
        eventKey: "of_share_owner",
      });
    }
  };

  // tracking for actual share
  const socialShareClick = (
    e: MouseEvent<HTMLElement>,
    key: keyof ShareURLsObjectType,
    url: string,
  ) => {
    if (!isBrowser) return;
    // do tracking

    // The mobile chrome browser does not allow js to open apps so
    // we are removing the event handler in this case.
    if (isChrome && isMobile) {
      // A hail mary in case prevent default on a link wont work with certain mobile user agents
      bumpPageShareCount().catch((err) => {
        trackError(err as Error, {
          function: "bumpShareCount",
        });
      });
      trackEventGtm({
        event: "share",
        action: key,
        entityType,
        isOwner,
      });
      return;
    } else {
      e.preventDefault();
      bumpPageShareCount().catch((err) => {
        trackError(err as Error, {
          function: "bumpShareCount",
        });
      });
      trackEventGtm({
        event: "share",
        action: key,
        entityType,
        isOwner,
      });

      if (!url?.length) return;

      // execute social share
      window.open(url, "_blank", "toolbar=0,status=0,width=548,height=400");
    }
  };

  const copyForShareClick = () => {
    bumpPageShareCount().catch((err) => {
      trackError(err as Error, {
        function: "bumpShareCount",
      });
    });
    trackEventGtm({
      event: "share",
      action: "copy url",
      entityType,
      isOwner,
    });
  };

  const nativeShare = () => {
    if (!isBrowser) return;
    // track share
    sendCtaTrackingEvent();
    bumpPageShareCount().catch((err) => {
      trackError(err as Error, {
        function: "bumpPageShareCount",
      });
    });

    const shareContentObject = getShareContentFromEntity(feedItem || page);
    // execute native share
    const navigatorShareObject = {
      title: shareContentObject.title,
      text: shareContentObject.description,
      url: shareContentObject.entityUrl,
    };
    window.navigator.share(navigatorShareObject).catch((err: Error) => {
      trackError(err, { function: "nativeShare" });
      setNativeShareIsOk(false);
    });
  };

  // this figures out what to share from url in browser, so we need window to exist
  // we want this to run once on mount, we don't want this to run on server or it was cause hydration issues
  useEffect(() => {
    if (!shareObject) {
      setShareObject(getShareURLsFromEntity(feedItem || page));
    }
    if (showMobile !== isMobile) {
      setShowMobile(isMobile);
    }
    if (showShareApi !== hasShareApi) {
      setShowShareApi(hasShareApi);
    }
  }, []);

  const content = () => {
    if (!shareObject) {
      return null;
    }
    return (
      <div className={sharedStyles["column"]}>
        <Box padding="xxs">
          <div className={styles["share-icon-wrapper"]}>
            <a
              className={styles["social-link"]}
              href={shareObject.facebook}
              target="_top"
              rel="noopener noreferrer"
              aria-label="share on facebook"
              onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                socialShareClick(e, "facebook", shareObject.facebook)
              }
              data-cta-type="share-facebook"
              style={{
                backgroundColor: "#4267b2",
              }}
            >
              <IconFa faIcon={faFacebookF} color="text-light" size="2rem" />
            </a>

            <a
              className={styles["social-link"]}
              href={shareObject.bluesky}
              target="_top"
              rel="noopener noreferrer"
              aria-label="share on bluesky"
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                socialShareClick(e, "bluesky", shareObject.bluesky);
              }}
              data-cta-type="share-bluesky"
              style={{
                backgroundColor: "rgb(10, 122, 255)",
              }}
            >
              <IconFa faIcon={faBluesky} color="text-light" size="2rem" />
            </a>

            <a
              className={styles["social-link"]}
              href={shareObject.twitter}
              target="_top"
              rel="noopener noreferrer"
              aria-label="share on twitter"
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                socialShareClick(e, "twitter", shareObject.twitter);
              }}
              data-cta-type="share-twitter"
              style={{
                backgroundColor: "#000000",
              }}
            >
              <IconFa faIcon={faXTwitter} color="text-light" size="2rem" />
            </a>

            {showMobile ? (
              <a
                className={styles["social-link"]}
                href={shareObject.whatsapp}
                target="_top"
                rel="noopener noreferrer"
                aria-label="share on whatsapp"
                onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                  socialShareClick(e, "whatsapp", shareObject.whatsapp)
                }
                data-cta-type="share-whatsapp"
                style={{
                  backgroundColor: "#25D366",
                }}
              >
                <IconFa faIcon={faWhatsapp} color="text-light" size="2rem" />
              </a>
            ) : null}

            {showMobile ? (
              <a
                className={styles["social-link"]}
                href={shareObject.fbmessenger}
                target="_top"
                rel="noopener noreferrer"
                aria-label="share on facebook messenger"
                onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                  socialShareClick(e, "fbmessenger", shareObject.fbmessenger)
                }
                data-cta-type="share-fbmessenger"
                style={{
                  backgroundColor: "#0078FF",
                }}
              >
                <IconFa
                  faIcon={faFacebookMessenger}
                  color="text-light"
                  size="2rem"
                />
              </a>
            ) : null}

            {showMobile ? (
              <a
                className={styles["social-link"]}
                href={shareObject.sms}
                target="_top"
                rel="noopener noreferrer"
                aria-label="share by sms"
                onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                  socialShareClick(e, "sms", shareObject.sms)
                }
                data-cta-type="share-sms"
                style={{
                  backgroundColor: "#53d769",
                }}
              >
                <IconFa faIcon={faComment} color="text-light" size="2rem" />
              </a>
            ) : null}

            <a
              className={styles["social-link"]}
              href={shareObject.linkedIn}
              target="_top"
              rel="noopener noreferrer"
              aria-label="share on linkedIn"
              onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                socialShareClick(e, "linkedIn", shareObject.linkedIn)
              }
              data-cta-type="share-linkedIn"
              style={{
                backgroundColor: "#0077b5",
              }}
            >
              <IconFa faIcon={faLinkedinIn} color="text-light" size="2rem" />
            </a>

            <a
              className={styles["social-link"]}
              href={shareObject.email}
              target="_top"
              rel="noopener noreferrer"
              aria-label="share with email"
              onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                socialShareClick(e, "email", shareObject.email)
              }
              data-cta-type="share-email"
              style={{
                backgroundColor: "var(--clr-primary, #00007e)",
              }}
            >
              <IconFa faIcon={faEnvelope} color="text-light" size="2rem" />
            </a>

            <button
              type="button"
              className={styles["social-link"]}
              aria-label="share with instagram"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                socialShareClick(e, "instagram", shareObject.instagram);
                setShowInstaHint((showInstaHint) => !showInstaHint);
              }}
              data-cta-type="instagram"
              style={{
                background:
                  "linear-gradient(to right, #833ab4,#fd1d1d,#fcb045)",
                borderWidth: 0,
                cursor: "pointer",
              }}
            >
              <IconFa faIcon={faInstagram} color="text-light" size="2rem" />
            </button>
          </div>
          <CopyToClipBoard
            shareCopy={shareObject.copy}
            onClick={copyForShareClick}
            data-cta-type="share-copy-url"
          />
          {showInstaHint ? (
            <aside>
              <Box paddingTop="s" style={{ maxWidth: "19rem" }}>
                <Heading
                  h2
                  textSize="m"
                  textFontFamily="base"
                  marginBottom="xs"
                >
                  Share on Instagram
                </Heading>
                {isMobile ? (
                  <Text
                    style={{
                      textWrap: "pretty",
                    }}
                  >
                    Copy the link above and share via:
                    <ul
                      style={{
                        paddingLeft: "var(--spacing-xs, 0.5rem)",
                        margin: "var(--spacing-xxs, 0.25rem)",
                      }}
                    >
                      <>
                        <li>
                          A direct message <IconFa faIcon={faPaperPlane} />
                        </li>
                        <li>
                          A link in your profile <IconFa faIcon={faLink} />
                        </li>
                        <li>
                          A Story with a link sticker{" "}
                          <IconFa faIcon={faNoteSticky} />
                        </li>
                      </>
                    </ul>
                  </Text>
                ) : (
                  <Text
                    style={{
                      textWrap: "pretty",
                    }}
                  >
                    {" "}
                    Copy the link above and share via direct messages
                  </Text>
                )}
              </Box>
            </aside>
          ) : null}
        </Box>
      </div>
    );
  };

  return (
    <div
      data-component={`share-${ctaLocation}`}
      style={style}
      className={className}
    >
      {nativeShareIsOk && showShareApi && isMobile ? (
        <>
          {React.cloneElement(children, {
            onClick: nativeShare,
          })}
        </>
      ) : (
        <PopOver
          modalContent={content()}
          modalLabel="share options"
          position={popoverPosition}
          fixedPosition={fixedPosition}
          onPopOverIsOpenChange={(isOpen: boolean) => {
            if (isOpen) {
              sendCtaTrackingEvent();
            }
          }}
        >
          {children}
        </PopOver>
      )}
    </div>
  );
};

export default Share;
